import { SiteUrls } from '@tableau/shared-urls';
import { CenteredFadeInSpinner } from '@tableau/vizportalclient-common-components';
import { ButtonType, ButtonWidget, HorizontalOriginOption, TextLinkWidget, VerticalOriginOption } from '@tableau/widgets';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';

import { Formatters, Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/common/react/CommonStyles';
import { ButtonWithUberDialog } from '~src/common/react/components/ButtonWithUberDialog';
import { Duration } from '~src/common/utilities/Duration';
import { imgPath } from '~src/common/utilities/ImgUrl';
import { getDatasourceUrl } from '~src/datasource/components/DatasourceSpace';
import { TestIds } from '~src/extract/components/AlertInfo';
import { spinnerStyles } from '~src/extract/styles/AlertInfoStyles';
import { openFlowTaskOutputsDialog } from '~src/flow/components/FlowTaskOutputsDialog';
import { FlowOverviewErrorsCellComponent, FlowParametersCellComponent, FlowTaskOutputStepsWithIconsCellComponent, FlowTaskStatusWithIconsCellComponent } from '~src/flow/FlowDataGridDefinitions';
import { Flows } from '~src/flow/resources/Flows';
import { FlowParameter } from '~src/flow/types/FlowParameters';
import { getAndOpenLinkedTaskDetailsDialog } from '~src/linkedTasks/components/LinkedTaskDetailsDialog';
import { cssClass, textEllipsis } from '~src/style';
import { FlowOutputStepInfo } from '~src/ts/models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo,FlowOutputStepRunType } from '~src/ts/models/FlowOutputStepRunInfo';
import { RunFlowTaskInfo } from '~src/ts/models/RunFlowTaskInfo';

import { SpacesSiteUrls } from '../../SpacesSiteUrls';
import { TooltipAnchor } from '../../Tooltips/TooltipAnchor';
import { StyledCellFormatter } from '../StyledCellWrapper';
import { PlainTextCellWithTransform } from './PlainTextAndNumberCells';

export function FlowRunTypeCell(): React.StatelessComponent<BodyCell.Props> {
  return PlainTextCellWithTransform('runType',
    (runType: FlowOutputStepRunType) => {
      return runType === FlowOutputStepRunType.scheduled ? Messages.flowRunHistoryColumn_runTypeScheduled() : Messages.flowRunHistoryColumn_runTypeOndemand();
    }
  );
}

export function FlowRefreshTypeCell(): React.StatelessComponent<BodyCell.Props> {
  return PlainTextCellWithTransform('isIncremental',
    (isIncremental: boolean) => {
      return isIncremental ? Messages.writeOptionsIncremental() : Messages.writeOptionsFull();
    }
  );
}

export function FlowRunDurationCell(
  startTimePropertyName: string,
  endTimePropertyName: string,
  nullDateTimeLabel?: string,
  testId?: string): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function DurationCell(props) {
      const startDateTime = props.row.data[startTimePropertyName];
      const endDateTime = props.row.data[endTimePropertyName];
      const duration = new Duration(startDateTime, endDateTime);
      const minimumTwoDigits = Intl.NumberFormat('en-US', { minimumIntegerDigits: 2 });
      if (startDateTime == null || endDateTime == null || duration.isNegative) {
        return nullDateTimeLabel == null ? null : <span data-tb-test-id={testId}>{nullDateTimeLabel}</span>;
      }
      return (
        <TooltipAnchor
          width={250}
          delay={0}
          title={Messages.flowRunHistoryColumn_duration()}
          tooltipContent={() => {
            /* eslint-disable jsx-a11y/anchor-has-content */
            /* eslint-disable jsx-a11y/anchor-is-valid */
            return (
              <div>
                <a href="" />
                <div className={'tb-content-item-popover-title'}>
                  {Messages.flowRunHistoryColumn_duration_title()}
                </div>
                <div className={'tb-content-item-popover-description'}>
                  {duration.hours > 0 ?
                    Messages.flowRunHistoryColumn_duration_message_hoursMinutesSeconds({
                      HOURS_COUNT: duration.hours,
                      HOURS_LOC: Formatters.formatCount(duration.hours),
                      MINUTES_COUNT: duration.minutes,
                      MINUTES_LOC: Formatters.formatCount(duration.minutes),
                      SECONDS_COUNT: duration.seconds,
                      SECONDS_LOC: Formatters.formatCount(duration.seconds),
                    }) : duration.minutes > 0 ?
                      Messages.flowRunHistoryColumn_duration_message_minutesSeconds({
                        MINUTES_COUNT: duration.minutes,
                        MINUTES_LOC: Formatters.formatCount(duration.minutes),
                        SECONDS_COUNT: duration.seconds,
                        SECONDS_LOC: Formatters.formatCount(duration.seconds),
                      }) :
                      Messages.flowRunHistoryColumn_duration_message_seconds({
                        SECONDS_COUNT: duration.seconds,
                        SECONDS_LOC: Formatters.formatCount(duration.seconds),
                      })
                  }
                </div>
              </div>
            );
          }
          }
        >
          <div data-tb-test-id={testId} style={CommonStyles.Layout.TextEllipsis}>
            {
              minimumTwoDigits.format(duration.hours) + ':' +
              minimumTwoDigits.format(duration.minutes) + ':' +
              minimumTwoDigits.format(duration.seconds)
            }
          </div>
        </TooltipAnchor>
      );
    }
  );
}

export function FlowTaskOutputStepsWithIconsCell(): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function FlowTaskOutputStepsWithIconsCell(props: BodyCell.Props) {
      const data = props.row.data;
      return (
        <FlowTaskOutputStepsWithIconsCellComponent flowId={data.flowId} outputSteps={data.flowOutputSteps} />
      );
    }
  );
}

interface FlowOrLinkedTaskOutputStepsWithIconsCellProps {
  title: string;
  iconUrl: string;
  iconClass?: string;
  iconTitle?: string;
  tooltipLinkText?: string;
  urls?: SpacesSiteUrls;
  row: DataGrid.Row;
}

const cellBodyStyle = cssClass('flow-output-steps', 'cell-body', {
  display: 'flex',
});

const dialogBodyStyle = cssClass('flow-output-steps', 'dialog-body', {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '450px',
});

const outputInfoStyle = cssClass('flow-output-steps', 'output-info', {
  margin: CommonStyles.Sizes.DefaultSpace,
});

const outputLinkStyle = cssClass('flow-output-steps', 'output-link', textEllipsis);

const iconStyle = cssClass('flow-output-steps', 'datasource-icon', {
  flex: 'none',
  paddingRight: CommonStyles.Sizes.DefaultSpace,
});

const spinnerStyle = cssClass('flow-p=output-steps', 'output-spinner', spinnerStyles);

export function FlowOrLinkedTaskOutputStepsWithIconsCell(urls?: SpacesSiteUrls): React.FunctionComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function FlowOrLinkedTaskOutputStepsWithIconsCell(props: BodyCell.Props) {
      const cellComponentProps = {
        title: Messages.outputStepsTooltip(),
        iconUrl: imgPath('datasource_18px-large.svg'),
        iconClass: 'tb-react-dg-hcol',
        tooltipLinkText: Messages.viewDetails(),
        urls: urls,
        row: props.row,
      };
      return (<FlowOrLinkedTaskOutputStepsWithIconsCellComponent {...cellComponentProps} />);
    }
  );
}

function FlowOrLinkedTaskOutputStepsWithIconsCellComponent(props: FlowOrLinkedTaskOutputStepsWithIconsCellProps): JSX.Element {
  return (
    <div className={cellBodyStyle}>
      <img src={props.iconUrl} alt={Messages.pageHeaderDatasources()} />
      <ButtonWithUberDialog
        dialogWidth={300}
        renderButton={showDialog => (
          <ButtonWidget
            testId={'flow-output-dialog-info-button'}
            buttonType={ButtonType.Icon}
            onClick={showDialog}
            title={'flow-outputs-more-info'}
          >
            {Messages.outputCount({ COUNT: props.row.data.flowOutputSteps.length, COUNT_LOC: Formatters.formatCount(props.row.data.flowOutputSteps.length) })}
          </ButtonWidget>
        )}
        uberDialogContent={<FlowOutputDialogContent {...props} />}
        anchorOrigin={{ horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.centerY }}
      />
    </div>
  );
}

function FlowOutputDialogContent(props: FlowOrLinkedTaskOutputStepsWithIconsCellProps): JSX.Element {
  const [outputStepInfos, setOutputStepInfos] = React.useState<ReadonlyArray<FlowOutputStepInfo>>([]);
  const [isFetchingOutputs, setIsFetchingOutputs] = React.useState<boolean>(true);
  const data = props.row.data;
  const isLinkedTask = data.taskType === 'LinkedTask';
  const isFlowTask = data.taskType === 'FlowTask';

  React.useEffect(
    () => {
      if (isLinkedTask && data.flowOutputSteps.length > 0) {
        const flowIds: string[] = data.flowOutputSteps.map((outputStep: FlowOutputStepInfo) => outputStep.flowId);
        const uniqueFlowIds: string[] = flowIds.filter((flowId, index, flowIds) => flowIds.indexOf(flowId) === index);
        uniqueFlowIds.map((flowId: string) => {
          Flows.getOutputStepInfos(flowId).then((outputSteps: FlowOutputStepInfo[]) => {
            setOutputStepInfos(c => c.concat(outputSteps));
          });
        });
      } else if (isFlowTask) {
        Flows.getOutputStepInfos(data.flowId).then((outputSteps) => {
          setOutputStepInfos(outputSteps);
        });
      } else if (data.flowOutputSteps.length === 0) {
        setIsFetchingOutputs(false);
      }
    },
    [data.flowOutputSteps, data.flowId, isLinkedTask, isFlowTask]
  );

  const outputStepCount = data.flowOutputSteps.length;
  const title = isLinkedTask ?
    Messages.linkedTasksOutputStepsTitle({
      OUTPUT_STEPS: outputStepCount,
      FLOWS: data.totalSteps,
      OUTPUT_STEPS_LOC: Formatters.formatCount(outputStepCount),
      FLOWS_LOC: Formatters.formatCount(data.totalSteps)
    })
    : Messages.outputCount({ COUNT: outputStepCount, COUNT_LOC: Formatters.formatCount(outputStepCount) });

  const Icon = () =>
    <span data-tb-test-id={'flow-output-cell-icon'}>
      <img data-tb-test-id={'flow-output-icon-img'}
        title={Messages.pageHeaderDatasources()}
        className={iconStyle}
        src={props.iconUrl}
        alt={'datasource-icon'} />
    </span>;

  const handleViewMoreDetailsClick = () => {
    if (isLinkedTask) {
      return getAndOpenLinkedTaskDetailsDialog(data.linkedTaskId).catch(() => {});
    } else if (isFlowTask) {
      return openFlowTaskOutputsDialog({
        outputSteps: data.flowOutputSteps,
        flowTask: data as RunFlowTaskInfo,
      }).catch(() => {});
    }
  };

  function getOutputStepData(): JSX.Element[] {
    if (outputStepInfos) {
      if (isFetchingOutputs && outputStepInfos.length === data.flowOutputSteps.length) {
        setIsFetchingOutputs(false);
      }
      const outputs = outputStepInfos.map((outputStep, i) =>
        <div
          key={'output-' + i}
          className={outputInfoStyle}>
          <Icon />
          <TextLinkWidget
            testId={'flow-output-link'}
            className={outputLinkStyle}
            href={getDatasourceUrl(props.urls, outputStep.datasourceId)}
          >
            {outputStep.datasourceName}
          </TextLinkWidget>
        </div>);
      return outputs;
    }
  }

  function getViewMoreLink(): JSX.Element {
    return (
      <TextLinkWidget
        testId={'flow-cell-details-link'}
        onClick={handleViewMoreDetailsClick}
        tabIndex={0}
      >
        {props.tooltipLinkText}
      </TextLinkWidget>);
  }
  return (
    <div className={dialogBodyStyle}>
      <div className={'tb-content-item-popover-title'}>
        {title}
      </div>
      {isFetchingOutputs && <div className={spinnerStyle}>
        <CenteredFadeInSpinner testId={TestIds.SPINNER} />
      </div>}
      {getOutputStepData()}
      {getViewMoreLink()}
    </div>
  );
}

export function FlowTaskStatusWithIconsCell(siteUrls: SiteUrls, flowFailureThresholdForRunPrevention: number): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function FlowTaskStatusWithIconsCell(props: BodyCell.Props) {
      const data = props.row.data;
      return (
        <FlowTaskStatusWithIconsCellComponent
          flowId={data.flowId}
          siteUrls={siteUrls}
          runFlowTaskInfo={data as RunFlowTaskInfo}
          flowFailureThresholdForRunPrevention={flowFailureThresholdForRunPrevention}
        />
      );
    }
  );
}

export function FlowRunHistoryErrorsCell(siteUrls: SiteUrls, isDownloadAllowed: boolean): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function FlowRunHistoryErrorsCell(props: BodyCell.Props) {
      const data = props.row.data;
      const isLinkedTask = data.taskType === 'LinkedTask';
      return (
        <FlowOverviewErrorsCellComponent
          errors={data.flowRunErrors}
          flowId={data.flowId}
          onConnectionsPage={false}
          siteUrls={siteUrls}
          isDownloadAllowed={isDownloadAllowed}
          isLinkedTask={isLinkedTask}
        />
      );
    }
  );
}

export function FlowScheduleTypeCell(): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function FlowScheduleTypeCell(props) {
      const data = props.row.data as RunFlowTaskInfo;
      const isLinkedTask = data.taskType === 'LinkedTask';

      function handleFlowScheduleTextLinkClick() {
        if (isLinkedTask) {
          return getAndOpenLinkedTaskDetailsDialog(data.linkedTaskId).catch(() => {});
        } else {
          return openFlowTaskOutputsDialog({
            outputSteps: data.flowOutputSteps,
            flowTask: data,
          }).catch(() => {});
        }
      }

      return (
        <span>
          <span>{Messages.flow()}</span>
          <TextLinkWidget
            testId={'FlowScheduleTypeCellTextLinkButton'}
            onClick={handleFlowScheduleTextLinkClick}
            style={{ fontWeight: 'normal', paddingLeft: '3px' }}
          >
            {isLinkedTask ?
              Messages.linkedTasksScheduleTypeCellLinkedTasksCount({ INDEX_LOC: data.stepNumber.toString(), TOTAL_COUNT_LOC: data.totalSteps.toString() }) :
              Messages.linkedTasksScheduleTypeCellOutputsCount({ OUTPUTS_COUNT: data.flowOutputSteps.length, OUTPUTS_COUNT_LOC: data.flowOutputSteps.length.toString() })}
          </TextLinkWidget>
        </span>
      );
    }
  );
}

export function FlowParameterWithOverrideCell(): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function FlowParameterWithOverrideCell(props) {
      const data = props.row.data as RunFlowTaskInfo;
      let parameters: FlowParameter[] = [];
      data.flowOutputSteps.forEach(step => {
        if(step.relevantParameters.length > 0) {
          parameters = parameters.concat(step.relevantParameters);
        }
      });
      return <FlowParametersCellComponent parameters={parameters} parameterRuns={data.relevantParameterRuns}/>;
    }
  );
}

export function FlowParameterRunWithOverrideCell(): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function FlowParameterRunWithOverrideCell(props) {
      const data = props.row.data as FlowOutputStepRunListItemInfo;
      return <FlowParametersCellComponent parameterRuns={data.relevantParameterRuns}/>;
    }
  );
}